import {
	infiniteQueryOptions,
	QueryClient,
	queryOptions,
	useInfiniteQuery,
	useQuery,
} from "@tanstack/react-query";
import { FindManyFilter } from "@onair/types/extends";
import { actionGetEventList } from "@onair/store/actions/event";
import { EventV2Response } from "@onair/repositories";

import {
	GET_PUBLIC_EVENTS,
	GET_EVENT_BY_ID_V2,
	GET_EVENT_BY_SLUG_V2,
	GET_MY_CO_HOST_EVENTS,
	GET_MY_CO_HOST_EVENTS_COUNTING,
	GET_EVENTS_BY_USERNAME,
	GET_PUBLIC_EVENTS_AND_COMBOS,
	GET_PUBLIC_EVENTS_AND_COMBOS_CACHE,
	GET_USERS_BUYED_TICKETS_EVENT_OFFLINE,
	EVENT_TRENDING_ON_EVENT_DETAIL,
	EVENT_TRENDING_ON_CART,
} from "../../keys";
import {
	getPublicEvents,
	getEventV2ById,
	getEventV2BySlug,
	getMyCoHostEvents,
	getMyCoHostEventsCounting,
	IGetPublicEventsV2Params,
	IGetMineEventsV2Params,
	IGetMyCoHostEventsCountingParams,
	getPublicEventsAndCombosCache,
	getPublishEventsAndCombos,
	getEventsAndCombosCached,
	PublishEventAndComboQueryFilter,
	getUsersBuyedTicketsEventOffline,
	EventOfflineSuccessFilterPayload,
	getEventTrendOnEventDetail,
	getEventTrendOnCart,
} from "@onair/services/event";

const useGetEventV2BySlug = (slug: string) =>
	useQuery({
		queryKey: [GET_EVENT_BY_SLUG_V2, slug],
		queryFn: () => getEventV2BySlug(slug),
	});

const useGetEventV2ById = (id: string, opts?: { enabled?: boolean }) => {
	const { enabled = true } = opts || {};
	return useQuery({
		queryKey: [GET_EVENT_BY_ID_V2, id],
		queryFn: () => getEventV2ById(id),
		enabled,
	});
};

const useGetMyCoHostEvents = (params: IGetMineEventsV2Params) =>
	useQuery({
		queryKey: [GET_MY_CO_HOST_EVENTS, params],
		queryFn: () => getMyCoHostEvents(params),
	});

const useGetMyCoHostEventsCounting = (
	params: IGetMyCoHostEventsCountingParams,
) =>
	useQuery({
		queryKey: [GET_MY_CO_HOST_EVENTS_COUNTING, params],
		queryFn: () => getMyCoHostEventsCounting(params),
	});

const useGetPublicEvents = (params: IGetPublicEventsV2Params) =>
	useQuery({
		queryKey: [GET_PUBLIC_EVENTS, params],
		queryFn: () => getPublicEvents(params),
	});

const useGetPublicEventsAndCombosCache = (params: IGetPublicEventsV2Params) =>
	useQuery({
		queryKey: [GET_PUBLIC_EVENTS_AND_COMBOS_CACHE, params],
		queryFn: () => getPublicEventsAndCombosCache(params),
	});

const useGetPublicEventsAndCombos = (params: IGetPublicEventsV2Params) =>
	useQuery({
		queryKey: [GET_PUBLIC_EVENTS_AND_COMBOS, params],
		queryFn: () => getPublishEventsAndCombos(params),
	});

const useGetEventsByUserName = ({ username }: { username: string }) => {
	return useQuery({
		queryKey: [GET_EVENTS_BY_USERNAME, username],
		queryFn: async () => {
			const query: FindManyFilter<EventV2Response> = {
				orderBy: {
					startDate: "asc",
				},
				where: {
					host: {
						username,
					},
					endDate: {
						gte: new Date(),
					},
					product: {
						isHidden: false,
					},
					status: "PUBLISHED",
				},
				take: Number.MAX_SAFE_INTEGER,
			};
			return await actionGetEventList(query);
		},
	});
};

const getEventInfiniteQueryOptions = (
	filter?: PublishEventAndComboQueryFilter,
) => {
	let queryKey: (string | PublishEventAndComboQueryFilter)[] = [
		GET_PUBLIC_EVENTS_AND_COMBOS,
	];

	if (filter) {
		let filterKeysValid: PublishEventAndComboQueryFilter = {};
		Object.entries(filter).forEach(([key, value]) => {
			if (filter[key as keyof PublishEventAndComboQueryFilter]) {
				filterKeysValid = {
					...filterKeysValid,
					[key]: value,
				};
			}
		});
		queryKey = [...queryKey, filterKeysValid];
	}
	console.log({ filter, queryKey });
	return infiniteQueryOptions({
		queryKey,
		initialPageParam: "",
		refetchOnMount: true,
		refetchOnWindowFocus: true,
		queryFn: ({ pageParam }) =>
			getEventsAndCombosCached({
				...filter,
				cursor: pageParam || undefined,
			}),
		getNextPageParam: ({ paging }, allPages) => paging.nextCursor,
	});
};
const usePrefetchEventAndComboCachedInfiniteQuery = (
	queryClient: QueryClient,
	filter?: PublishEventAndComboQueryFilter,
) => queryClient.prefetchInfiniteQuery(getEventInfiniteQueryOptions(filter));
const useEventAndComboCachedInfiniteQuery = (
	filter?: PublishEventAndComboQueryFilter,
) =>
	useInfiniteQuery({
		...getEventInfiniteQueryOptions(filter),
		placeholderData: (previousData, previousQuery) => previousData,
	});

/**
 * @param itemId
 * @type string;
 * - If Event single the value should be id of event.
 * - If Event session the value should be id of event sessions
 */
const useGetUsersBuyedTicketsEventOfflineQuery = (
	payload: EventOfflineSuccessFilterPayload,
) => {
	return useQuery({
		queryKey: [GET_USERS_BUYED_TICKETS_EVENT_OFFLINE, { ...payload }],
		queryFn: () => getUsersBuyedTicketsEventOffline(payload),
	});
};
const useGetEventTrendOnEventDetail = (eventId: string) => {
	return useQuery({
		queryKey: [EVENT_TRENDING_ON_EVENT_DETAIL, eventId],
		queryFn: () => getEventTrendOnEventDetail(eventId)
	})
}

const useGetEventTrendOnCart = (userId: string) => {
	return useQuery({
		queryKey: [EVENT_TRENDING_ON_CART, userId],
		queryFn: () => getEventTrendOnCart()
	})
}


export {
	useGetPublicEvents,
	useGetMyCoHostEvents,
	useGetMyCoHostEventsCounting,
	useGetEventV2BySlug,
	useGetEventV2ById,
	useGetEventsByUserName,
	useGetPublicEventsAndCombosCache,
	useGetPublicEventsAndCombos,
	usePrefetchEventAndComboCachedInfiniteQuery,
	useEventAndComboCachedInfiniteQuery,
	useGetUsersBuyedTicketsEventOfflineQuery,
	useGetEventTrendOnEventDetail,
	useGetEventTrendOnCart
};
